import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useStores } from '~store/utils';
import SiteStore from '~/modules/sites/store/Site';

const URL_PARAM = 'site';
// Routes whose path already encodes the site uuid: /sites/<uuid>/...
const PATH_SCOPED_RE = /^\/sites\/[^/]+/;

export const useSelectedSite = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { sites, permissions } = useStores();
  const isPathScoped = PATH_SCOPED_RE.test(location.pathname);
  const canViewSite = permissions.has('can_view_site');

  React.useEffect(() => {
    // Path encodes the site; siteWrapper drives selection. Strip any stale ?site=.
    if (isPathScoped) {
      if (searchParams.has(URL_PARAM)) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(URL_PARAM);
            return next;
          },
          { replace: true }
        );
      }
      return;
    }

    // has() is false until permissions load, which would strip ?site= on a refresh.
    if (!permissions.loaded) return;

    if (!canViewSite) {
      // No site permission → never expose ?site= in URL (clean stale ones too).
      if (searchParams.has(URL_PARAM)) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(URL_PARAM);
            return next;
          },
          { replace: true }
        );
      }
      return;
    }

    if (!sites.loaded || sites.sites.length === 0) return;

    const fromUrl = searchParams.get(URL_PARAM);
    if (fromUrl && sites.sites.some((s) => s.uuid === fromUrl)) {
      if (sites.selectedSiteUuid !== fromUrl) sites.setSelectedSiteUuid(fromUrl);
      return;
    }
    if (sites.selectedSiteUuid !== null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(URL_PARAM, sites.selectedSiteUuid!);
          return next;
        },
        { replace: true }
      );
    }
  }, [
    isPathScoped,
    permissions.loaded,
    canViewSite,
    sites,
    sites.loaded,
    sites.sites.length,
    sites.selectedSiteUuid,
    searchParams,
    setSearchParams,
  ]);

  const setSelectedSiteUuid = React.useCallback(
    (uuid: string | null) => {
      sites.setSelectedSiteUuid(uuid);
      // On path-scoped pages, switching site means navigating to that site's root
      // (current path's nested ids — plane, etc. — won't exist on the new site).
      if (isPathScoped) {
        if (uuid) navigate(`/sites/${uuid}`);
        return;
      }
      if (!canViewSite) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (uuid === null) next.delete(URL_PARAM);
        else next.set(URL_PARAM, uuid);
        return next;
      });
    },
    [sites, setSearchParams, isPathScoped, navigate, canViewSite]
  );

  return [sites.selectedSite as SiteStore | null, setSelectedSiteUuid] as const;
};

/**
 * Switches the selected site to the one an entity page belongs to, e.g. a resident opened from a
 * link or another site. Replaces the history entry and keeps the tab hash, so going back returns
 * to the previous page and its own ?site=. Applied once per entity site, so picking another site
 * in the selector afterwards is not undone.
 */
export const useFollowSite = (siteUuid: string | null | undefined) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sites, permissions } = useStores();
  const canViewSite = permissions.has('can_view_site');
  const followed = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!siteUuid || followed.current === siteUuid || !permissions.loaded) return;
    if (!sites.sites.some((s) => s.uuid === siteUuid)) return;
    followed.current = siteUuid;

    sites.setSelectedSiteUuid(siteUuid);
    const params = new URLSearchParams(location.search);
    if (!canViewSite || params.get(URL_PARAM) === siteUuid) return;
    params.set(URL_PARAM, siteUuid);
    navigate({ search: params.toString(), hash: location.hash }, { replace: true });
  }, [siteUuid, sites, sites.sites.length, permissions.loaded, canViewSite, location.search, location.hash, navigate]);
};
